import {
  Tooltip,
  Stack,
  Typography,
  Select,
  MenuItem,
  FormControl,
  Checkbox,
  FormControlLabel,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  useTheme,
  Box,
  useMediaQuery,
} from "@mui/material";
import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import RestoreIcon from "@mui/icons-material/Restore";
import IconButton from "@mui/material/IconButton";
// Imports from common components
import CommonAgGrid from "./common/CommonAgGrid/CommonAgGrid";

import { apiFetch } from "../utils/auth";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import dayjs from "dayjs";
import { CustomTooltipCell } from "./common/CustomTooltip";
import BackgroundContainer from "./common/BackgroundContainer";
import { getFilteredColumnDefs, mergeLMPByHourEnding } from "../utils";
import CustomDatePicker from "altopenergy.customdatepicker";

const PJM = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const red = theme.palette.customColors.pnlRed;
  const green = theme.palette.customColors.pnlGreen;
  // States
  const [countdown, setCountdown] = useState(15);
  const [CurrentTime, setCurrentTime] = useState("");
  const [LoadForecastDataPJM, setLoadForecastDataPJM] = useState([]);
  const [LoadForecastPJM_ColumnDefs, setLoadForecastPJM_ColumnDefs] = useState(
    [],
  );
  const [OnPeakHours, setOnPeakHours] = useState([]);
  const [OffPeakHours, setOffPeakHours] = useState([]);
  const [WePeakHours, setWePeakHours] = useState([]);
  const [CustomModifiedRows, setCustomModifiedRows] = useState([]);
  const [DALMP, setDALMP] = useState([]);
  const [DALMP_AVG, setDALMP_AVG] = useState("");
  const [DALMP_ColumnDefs, setDALMP_ColumnDefs] = useState([
    {
      field: "HE",
      headerName: "HE",
      flex: 1,
      cellStyle: (params) => {
        return { fontSize: "16px" };
      },
    },
    {
      field: "LMP",
      headerName: "LMP",
      flex: 1,
      cellStyle: (params) => {
        return { fontSize: "16px" };
      },
    },
  ]);
  const [DayAheadRowData, setDayAheadRowData] = useState([]);
  const [DAPublishedTime_CST, setDAPublishedTime_CST] = useState("");
  const [LatestPublishedTime_CST, setLatestPublishedTime_CST] = useState("");
  const [DAClass, setDAClass] = useState("OnPeak");

  const [Hybrid, setHybrid] = useState([]);
  const [Hybrid_ColumnDefs, setHybrid_ColumnDefs] = useState([
    { field: "LMP", headerName: "LMP", flex: 1 },
    {
      field: "Ticks",
      headerName: "Ticks",
      flex: 1,
      cellRenderer: (params) => {
        const rtlmp = params.value;
        const row = params.data;

        const tableData = row.RTLMP_Intervals;
        return (
          <CustomTooltipCell
            tooltipContent={
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Interval</TableCell>
                      <TableCell>LMP</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tableData
                      .slice()
                      .reverse()
                      .map((row, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{row.Interval}</TableCell>
                          <TableCell sx={{ color: row.LMP > 0 ? green : red }}>
                            {row.LMP !== null
                              ? Math.round(row.LMP * 10) / 10
                              : ""}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            }
            value={<Box>{Math.round(rtlmp * 10) / 10}</Box>}
          />
        );
      },
    },
  ]);
  const [Custom, setCustom] = useState([]);
  const [Custom_ColumnDefs, setCustom_ColumnDefs] = useState([
    {
      field: "LMP",
      headerName: "LMP",
      flex: 1,
      editable: (params) => {
        const he = params.data.HourEnding;

        // Rule 1: Hybrid mode + current HE → disable
        if (useDAHybrid && he === currentHE) return false;

        // Rule 2: Disable for past hours
        if (he < currentHE) return false;

        // Otherwise editable
        return true;
      },
    },
    {
      field: "Ticks",
      headerName: "Ticks",
      flex: 1,
      cellRenderer: (params) => {
        const rtlmp = params.value;
        const row = params.data;

        const tableData = row.RTLMP_Intervals;
        return (
          <CustomTooltipCell
            tooltipContent={
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Interval</TableCell>
                      <TableCell>LMP</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tableData
                      .slice()
                      .reverse()
                      .map((row, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{row.Interval}</TableCell>
                          <TableCell sx={{ color: row.LMP > 0 ? green : red }}>
                            {row.LMP !== null
                              ? Math.round(row.LMP * 10) / 10
                              : ""}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            }
            value={<Box>{Math.round(rtlmp * 10) / 10}</Box>}
          />
        );
      },
    },
  ]);

  const [Hybrid_AVG, setHybrid_AVG] = useState("");
  const [HybridRowData, setHybridRowData] = useState([]);
  const [PJMDispatch, setPJMDispatch] = useState([]);
  const [PJMDispatch_ColumnDefs, setPJMDispatch_ColumnDefs] = useState([
    {
      field: "StartTime",
      headerName: "Start Time",
      flex: 1,
      cellStyle: (params) => ({
        fontSize: "16px",
      }),
    },
    {
      field: "EndTime",
      headerName: "End Time",
      flex: 1,
      cellStyle: (params) => ({
        fontSize: "16px",
      }),
    },
    {
      field: "Price",
      headerName: "Price",
      flex: 1,
      cellStyle: (params) => {
        // Get the previous row value for the comparison
        const previousRow = params.api.getDisplayedRowAtIndex(
          params.rowIndex + 1,
        );

        const previousPrice = previousRow ? previousRow.data.Price : null;
        const currentPrice = params.value;

        // Compare current row value with previous row's value
        if (previousPrice !== null) {
          // Convert to numbers to ensure we are comparing numbers, not strings
          const currentPriceNum = Number(currentPrice);
          const previousPriceNum = Number(previousPrice);

          // Check if both values are valid numbers before proceeding
          if (!isNaN(currentPriceNum) && !isNaN(previousPriceNum)) {
            if (currentPriceNum < previousPriceNum) {
              return {
                fontSize: "16px",
                color: "#FF0000",
                backgroundColor:
                  theme.palette.mode === "light" ? "#fff" : "#2a2a2a",
              }; // If current < previous, color red
            } else if (currentPriceNum > previousPriceNum) {
              return {
                fontSize: "16px",
                color: "#30bf35",
                backgroundColor:
                  theme.palette.mode === "light" ? "#fff" : "#2a2a2a",
              }; // If current > previous, color green
            }
          }
        }

        return { fontSize: "16px" }; // No style if no previous value to compare
      },
    },
  ]);
  const [PJMLatestDispatchTimestamp, setPJMLatestDispatchTimestamp] =
    useState("");
  const [PJMDispatchLMPs, setPJMDispatchLMPs] = useState([]);
  const [PJMDispatchLMP_ColumnDefs, setPJMDispatchLMP_ColumnDefs] = useState([
    {
      field: "Interval",
      headerName: "Time",
      flex: 1.2,
      cellStyle: (params) => ({
        fontSize: "16px",
      }),
    },
    {
      field: "Price",
      headerName: "Dispatch",
      flex: 1.3,
      cellStyle: (params) => {
        // Get the previous row value for the comparison
        const previousRow = params.api.getDisplayedRowAtIndex(
          params.rowIndex + 1,
        );

        const previousPrice = previousRow ? previousRow.data.Price : null;
        const currentPrice = params.value;

        // Compare current row value with previous row's value
        if (previousPrice !== null) {
          // Convert to numbers to ensure we are comparing numbers, not strings
          const currentPriceNum = Number(currentPrice);
          const previousPriceNum = Number(previousPrice);

          // Check if both values are valid numbers before proceeding
          if (!isNaN(currentPriceNum) && !isNaN(previousPriceNum)) {
            if (currentPriceNum < previousPriceNum) {
              return {
                fontSize: "16px",
                color: "#FF0000",
                backgroundColor:
                  theme.palette.mode === "light" ? "#fff" : "#2a2a2a",
              }; // If current < previous, color red
            } else if (currentPriceNum > previousPriceNum) {
              return {
                fontSize: "16px",
                color: "#30bf35",
                backgroundColor:
                  theme.palette.mode === "light" ? "#fff" : "#2a2a2a",
              }; // If current > previous, color green
            }
          }
        }

        return { fontSize: "16px" }; // No style if no previous value to compare
      },
    },
    {
      field: "LMP",
      headerName: "LMP",
      flex: 1,
      cellStyle: (params) => {
        // Get the previous row value for the comparison
        const previousRow = params.api.getDisplayedRowAtIndex(
          params.rowIndex + 1,
        );
        const previousLMP = previousRow ? previousRow.data.LMP : null;
        const currentLMP = params.value;

        // Compare current row value with previous row's value for LMP
        if (previousLMP !== null) {
          // Convert to numbers to ensure we are comparing numbers, not strings
          const currentLMPNum = Number(currentLMP);
          const previousLMPNum = Number(previousLMP);

          // Check if both values are valid numbers before proceeding
          if (!isNaN(currentLMPNum) && !isNaN(previousLMPNum)) {
            if (currentLMPNum < previousLMPNum) {
              return {
                fontSize: "16px",
                color: "#FF0000",
                backgroundColor:
                  theme.palette.mode === "light" ? "#fff" : "#2a2a2a",
              }; // If current < previous, color red
            } else if (currentLMPNum > previousLMPNum) {
              return {
                fontSize: "16px",
                color: "#30bf35",
                backgroundColor:
                  theme.palette.mode === "light" ? "#fff" : "#2a2a2a",
              }; // If current > previous, color green
            }
          }
        }

        return { fontSize: "16px" }; // No style if no previous value to compare
      },
    },
  ]);
  const [LatestDispatch, setLatestDispatch] = useState([]);
  const [LatestDispatch_ColumnDefs, setLatestDispatch_ColumnDefs] = useState([
    {
      field: "Zone",
      headerName: "Zone",
      flex: 1,
      cellStyle: (params) => {
        const row = params.data;
        const zone = row.Zone;
        if (zone === "PEP" || zone === "PENELEC") {
          return {
            backgroundColor: "#90CAF9",
            color: "black",
            fontSize: "16px",
          };
        } else {
          return { fontSize: "16px" };
        }
      },
    },
    {
      field: "Rate",
      headerName: "Rate",
      flex: 1,
      cellStyle: (params) => {
        const row = params.data;
        const zone = row.Zone;
        if (zone === "PEP" || zone === "PENELEC") {
          return {
            backgroundColor: "#90CAF9",
            color: "black",
            fontSize: "16px",
          };
        } else {
          return { fontSize: "16px" };
        }
      },
    },
  ]);
  const [LatestDispatchPrice, setLatestDispatchPrice] = useState("");
  const [LatestDispatchDelta, setLatestDispatchDelta] = useState("");
  const [PJMAce, setPJMAce] = useState("");
  const [PJMAceTimestamp, setPJMAceTimestamp] = useState("");
  const [PJMAceDelta, setPJMAceDelta] = useState("");
  const [flowDate, setflowDate] = useState(dayjs());
  const [useDispatch, setuseDispatch] = useState(true);
  const [useDAHybrid, setuseDAHybrid] = useState(true);
  const [currentHE, setcurrentHE] = useState("");
  const [DAHybrid, setDAHybrid] = useState("");
  const dateRef = useRef();

  //  Api Calls
  const funcFetchLoadForecast = (shouldTrack) => {
    apiFetch("balday/getLoadForecastPJM", {
      method: "POST",
      body: JSON.stringify({
        FlowDate: dateRef.current || flowDate.format("YYYY-MM-DD"),
      }),
      track: shouldTrack,
    })
      .then((res) => res.json())
      .then((res) => {
        setOnPeakHours(res.OnPeakHours);
        setOffPeakHours(res.OffPeakHours);
        setWePeakHours(res.WePeakHours);
        setLoadForecastDataPJM(res.LoadForecastData);
        setDAPublishedTime_CST(res.DAPublishedTime_CST);
        setLatestPublishedTime_CST(res.LatestPublishedTime_CST);
      })
      .catch((err) => {
        setLoadForecastDataPJM([]);
      });
  };
  const funcFetchDALMP = (shouldTrack) => {
    apiFetch("balday/getTopTables", {
      method: "POST",
      body: JSON.stringify({
        ISO: "PJM",
        FlowDate: dateRef.current || flowDate.format("YYYY-MM-DD"),
      }),
      track: shouldTrack,
    })
      .then((res) => res.json())
      .then((res) => {
        setDALMP(res.DALMP);
        setDALMP_AVG(res.DALMP_AVG);
        setHybrid(res.Hybrid);

        setPJMAce(res.AceValue);
        setPJMAceTimestamp(res.AceTime);
        setPJMAceDelta(res.AceDelta);
        setPJMDispatch(res.PJMDispatchPrices);
        setPJMLatestDispatchTimestamp(res.PJMDispatchTimestamp);
        setPJMDispatchLMPs(res.PJMDispatchLMPs);
        setLatestDispatch(res.LatestDispatch);
        setLatestDispatchPrice(res.LatestDispatchPrice);
        setLatestDispatchDelta(res.LatestDispatchPriceDelta);
        setDAHybrid(res.DAHybridValue);
        setcurrentHE(res.CurrentHourEnding);
      })
      .catch((err) => {
        setLoadForecastDataPJM([]);
      });
  };
  const funcModifyCustom = (shouldTrack) => {
    apiFetch("balday/modifyCustom", {
      method: "POST",
      body: JSON.stringify({
        Custom: Hybrid,
        PJMDispatchLMPs: PJMDispatchLMPs,
        ISO: "PJM",
        HourEnding: currentHE,
      }),
      track: shouldTrack,
    })
      .then((res) => res.json())
      .then((res) => {
        setCustom(
          mergeLMPByHourEnding(res.Custom, CustomModifiedRows, currentHE),
        );
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const util_generateColumnDefs = (data) => {
    const fixedCols = ["Zone", "Type"];

    // Base columns
    const baseColDefs = [
      {
        field: "Zone",
        headerName: "Zone",
        width: isMobile ? 100 : 150,
        pinned: "left",
        cellStyle: () => ({ fontSize: "14px" }),
      },
      {
        field: "Type",
        headerName: "Type",
        width: isMobile ? 100 : 150,
        pinned: "left",
        cellStyle: () => ({ fontSize: "14px" }),
      },
    ];

    // Select hours by class
    let selectedHours = [];
    switch (DAClass?.toLowerCase()) {
      case "onpeak":
        selectedHours = OnPeakHours || [];
        break;
      case "offpeak":
        selectedHours = OffPeakHours || [];
        break;
      case "wepeak":
        selectedHours = WePeakHours || [];
        break;
      case "atc":
        selectedHours = [
          1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20,
          21, 22, 23, 24,
        ];
        break;
      default:
        selectedHours = [];
    }

    // Convert to "01" format
    const allowedCols = selectedHours.map((h) => String(h).padStart(2, "0"));

    // Build hour columns
    const additionalColDefs = Object.keys(data[0])
      ?.filter((key) => !fixedCols.includes(key))
      ?.filter((key) => allowedCols.includes(key))
      ?.sort((a, b) => Number(a) - Number(b))
      ?.map((key) => ({
        field: key,
        headerName: `HE${key}`,
        width: isMobile ? 90 : 95,
        cellStyle: () => {
          if (key == currentHE) {
            return {
              backgroundColor: "#90CAF9",
              color: "#000",
              fontWeight: "bold",
              fontSize: "14px",
            };
          }
          return { fontSize: "14px" };
        },
      }));
    setLoadForecastPJM_ColumnDefs([...baseColDefs, ...additionalColDefs]);
  };

  useEffect(() => {
    if (Array.isArray(LoadForecastDataPJM) && LoadForecastDataPJM.length > 0) {
      util_generateColumnDefs(LoadForecastDataPJM);
    }
  }, [
    LoadForecastDataPJM,
    currentHE,
    DAClass,
    OnPeakHours,
    OffPeakHours,
    WePeakHours,
    isMobile,
  ]);

  useEffect(() => {
    if (!DALMP) return;

    if (DAClass === "OnPeak") {
      const updatedData = DALMP.filter((row) => row.OnPeak);
      const meanLMP = (
        updatedData.reduce((sum, row) => sum + row.LMP, 0) / updatedData.length
      ).toFixed(2);
      setDayAheadRowData(DALMP.filter((row) => row.OnPeak));
      setDALMP_AVG(meanLMP);
    } else if (DAClass === "OffPeak") {
      const updatedData = DALMP.filter((row) => !row.OnPeak);
      const meanLMP = (
        updatedData.reduce((sum, row) => sum + row.LMP, 0) / updatedData.length
      ).toFixed(2);
      setDayAheadRowData(DALMP.filter((row) => !row.OnPeak));
      setDALMP_AVG(meanLMP);
    } else if (DAClass === "WePeak") {
      const updatedData = DALMP.filter((row) => row.WePeak);
      const meanLMP = (
        updatedData.reduce((sum, row) => sum + row.LMP, 0) / updatedData.length
      ).toFixed(2);
      setDayAheadRowData(DALMP.filter((row) => row.WePeak));
      setDALMP_AVG(meanLMP);
    } else {
      const meanLMP = (
        DALMP.reduce((sum, row) => sum + row.LMP, 0) / DALMP.length
      ).toFixed(2);
      setDayAheadRowData(DALMP);
      setDALMP_AVG(meanLMP);
    }
  }, [DALMP, DAClass]);

  useEffect(() => {
    if (!Hybrid) return;

    if (DAClass === "OnPeak") {
      const updatedData = Hybrid.filter((row) => row.OnPeak);
      setHybridRowData(updatedData);
      setHybrid_AVG(
        (
          updatedData.reduce((sum, row) => sum + row.LMP, 0) /
          updatedData.length
        ).toFixed(2),
      );
    } else if (DAClass === "OffPeak") {
      const updatedData = Hybrid.filter((row) => !row.OnPeak);
      setHybridRowData(updatedData);
      setHybrid_AVG(
        (
          updatedData.reduce((sum, row) => sum + row.LMP, 0) /
          updatedData.length
        ).toFixed(2),
      );
    } else if (DAClass === "WePeak") {
      const updatedData = Hybrid.filter((row) => row.WePeak);
      setHybridRowData(updatedData);
      setHybrid_AVG(
        (
          updatedData.reduce((sum, row) => sum + row.LMP, 0) /
          updatedData.length
        ).toFixed(2),
      );
    } else {
      setHybridRowData(Hybrid);
      setHybrid_AVG(
        (Hybrid.reduce((sum, row) => sum + row.LMP, 0) / Hybrid.length).toFixed(
          2,
        ),
      );
    }
  }, [Hybrid, DAClass]);

  const Custom_RowData = useMemo(() => {
    if (!Custom || Custom.length === 0) return [];

    // Step 1: Apply DAHybrid for currentHE if needed
    let data = Custom.map((item) =>
      useDAHybrid && item.HourEnding == currentHE
        ? { ...item, LMP: DAHybrid }
        : item,
    );

    // Step 2: Filter based on DAClass
    if (DAClass === "OnPeak") {
      data = data.filter((row) => row.OnPeak);
    } else if (DAClass === "OffPeak") {
      data = data.filter((row) => !row.OnPeak);
    } else if (DAClass === "WePeak") {
      data = data.filter((row) => row.WePeak);
    }

    return data;
  }, [Custom, useDAHybrid, currentHE, DAHybrid, DAClass]);

  // Calculate average LMP for filtered data
  const Custom_AVG = useMemo(() => {
    if (!Custom_RowData || Custom_RowData.length === 0) return 0;

    const total = Custom_RowData.reduce(
      (sum, row) => sum + Number(row.LMP || 0),
      0,
    );
    return (total / Custom_RowData.length).toFixed(2);
  }, [Custom_RowData]);
  useEffect(() => {
    // --- First fetch (with true) ---

    // --- Set initial countdown ---
    setCountdown(15);

    const intervalId = setInterval(() => {
      // --- Update CST Time every second ---
      const nowCST = new Date().toLocaleTimeString("en-US", {
        timeZone: "America/Chicago",
        hour12: false,
      });
      setCurrentTime(nowCST);

      // --- Countdown + auto-fetch ---
      setCountdown((prev) => {
        if (prev === 1) {
          // Time to refresh (with false)
          funcFetchDALMP(false);
          return 15; // reset countdown
        }
        return prev - 1; // tick down
      });
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (useDispatch) {
      funcModifyCustom(false);
    } else {
      setCustom(mergeLMPByHourEnding(Hybrid, CustomModifiedRows, currentHE));
    }
  }, [useDispatch, Hybrid]);

  useEffect(() => {
    dateRef.current = flowDate.format("YYYY-MM-DD");
    funcFetchDALMP(true);
    funcFetchLoadForecast(true);
  }, [flowDate]);

  const handleCellValueChanged = (params) => {
    const { oldValue, newValue, colDef, node } = params;

    // Check if the column changed is "LMP"
    if (colDef.field === "LMP") {
      console.log("Old Value:", oldValue);
      console.log("New Value:", newValue);

      // Find the row being updated using HourEnding as the unique key
      const updatedRowData = Custom.map((row) => {
        if (row.HourEnding === node.data.HourEnding) {
          // Update the LMP field for the row with matching HourEnding
          return { ...row, LMP: newValue };
        }
        return row;
      });
      setCustomModifiedRows(JSON.parse(JSON.stringify(updatedRowData)));
      // Update state with the new row data
      setCustom(updatedRowData);
    }
  };

  useEffect(() => {
    setCustom_ColumnDefs([
      {
        field: "LMP",
        headerName: "LMP",
        flex: 1,
        cellStyle: (params) => {
          const row = params.data;
          const hourEnding = row.HourEnding;
          const ticks = row.Ticks;

          if (hourEnding === currentHE) {
            return {
              backgroundColor: "#90CAF9",
              color: "black",
              fontSize:
                DAClass === "OnPeak" || DAClass === "WePeak" ? "20px" : "16px",
            };
          } else if (hourEnding < currentHE && ticks < 12) {
            return {
              backgroundColor: "#f9e690ff",
              color: "black",
              fontSize:
                DAClass === "OnPeak" || DAClass === "WePeak" ? "20px" : "16px",
            };
          } else {
            return {
              fontSize:
                DAClass === "OnPeak" || DAClass === "WePeak" ? "20px" : "16px",
            };
          }
        },

        editable: (params) => {
          const he = params.data.HourEnding;

          // Rule 1: Hybrid mode + current HE → disable
          if (useDAHybrid && he === currentHE) return false;

          // Rule 2: Disable for past hours
          if (he < currentHE) return false;

          // Otherwise editable
          return true;
        },
        cellRenderer: (params) => {
          const value = params.value;
          const hourEnding = params.data.HourEnding;
          const showBoder = hourEnding >= Number(currentHE);

          return (
            <Box
              sx={{
                border: showBoder
                  ? theme.palette.mode === "dark"
                    ? "1px solid #ffffffbd"
                    : "1px solid #222222bd"
                  : "none",
                width: "100%",
              }}
            >
              {value}
            </Box>
          );
        },
        cellEditor: "agNumberCellEditor",
        suppressKeyboardEvent: (params) => {
          const key = params.event.key;

          if (params.editing && (key === "ArrowUp" || key === "ArrowDown")) {
            // completely block editor from receiving arrow keys
            params.event.preventDefault();
            return true;
          }

          return false;
        },
      },
      {
        field: "Ticks",
        headerName: "Ticks",
        flex: 1,
        cellStyle: (params) => {
          const row = params.data;
          const hourEnding = row.HourEnding;
          const ticks = params.value;

          if (hourEnding === currentHE) {
            return {
              backgroundColor: "#90CAF9",
              color: "black",
              fontSize:
                DAClass === "OnPeak" || DAClass === "WePeak" ? "20px" : "16px",
            };
          } else if (hourEnding < currentHE && ticks < 12) {
            return {
              backgroundColor: "#f9e690ff",
              color: "black",
              fontSize:
                DAClass === "OnPeak" || DAClass === "WePeak" ? "20px" : "16px",
            };
          } else {
            return {
              fontSize:
                DAClass === "OnPeak" || DAClass === "WePeak" ? "20px" : "16px",
            };
          }
        },
        cellRenderer: (params) => {
          const rtlmp = params.value;
          const row = params.data;
          const hourEnding = row.HourEnding;
          const tableData = row.RTLMP_Intervals;

          return (
            <CustomTooltipCell
              tooltipContent={
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Interval</TableCell>
                        <TableCell>LMP</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tableData.map((row, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{row.Interval}</TableCell>
                          <TableCell sx={{ color: row.LMP > 0 ? green : red }}>
                            {row.LMP !== null
                              ? Math.round(row.LMP * 10) / 10
                              : ""}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              }
              value={<Box>{rtlmp}</Box>}
            />
          );
        },
      },
    ]);
    setHybrid_ColumnDefs([
      {
        field: "LMP",
        headerName: "LMP",
        flex: 1,
        cellStyle: (params) => {
          const row = params.data;
          const hourEnding = row.HourEnding;
          const ticks = row.Ticks;

          if (hourEnding === currentHE) {
            return {
              backgroundColor: "#90CAF9",
              color: "black",
              fontSize:
                DAClass === "OnPeak" || DAClass === "WePeak" ? "20px" : "16px",
            };
          } else if (hourEnding < currentHE && ticks < 12) {
            return {
              backgroundColor: "#f9e690ff",
              color: "black",
              fontSize:
                DAClass === "OnPeak" || DAClass === "WePeak" ? "20px" : "16px",
            };
          } else {
            return {
              fontSize:
                DAClass === "OnPeak" || DAClass === "WePeak" ? "20px" : "16px",
            };
          }
        },
      },
      {
        field: "Ticks",
        headerName: "Ticks",
        flex: 1,
        cellStyle: (params) => {
          const row = params.data;
          const hourEnding = row.HourEnding;
          const ticks = params.value;

          if (hourEnding === currentHE) {
            return {
              backgroundColor: "#90CAF9",
              color: "black",
              fontSize:
                DAClass === "OnPeak" || DAClass === "WePeak" ? "20px" : "16px",
            };
          } else if (hourEnding < currentHE && ticks < 12) {
            return {
              backgroundColor: "#f9e690ff",
              color: "black",
              fontSize:
                DAClass === "OnPeak" || DAClass === "WePeak" ? "20px" : "16px",
            };
          } else {
            return {
              fontSize:
                DAClass === "OnPeak" || DAClass === "WePeak" ? "20px" : "16px",
            };
          }
        },
        cellRenderer: (params) => {
          const rtlmp = params.value;
          const row = params.data;
          const hourEnding = row.HourEnding;

          const tableData = row.RTLMP_Intervals;
          return (
            <CustomTooltipCell
              tooltipContent={
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Interval</TableCell>
                        <TableCell>LMP</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tableData.map((row, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{row.Interval}</TableCell>
                          <TableCell sx={{ color: row.LMP > 0 ? green : red }}>
                            {row.LMP !== null
                              ? Math.round(row.LMP * 10) / 10
                              : ""}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              }
              value={
                <Box>
                  {rtlmp} {hourEnding < Number(currentHE) ? "✓" : ""}
                </Box>
              }
            />
          );
        },
      },
    ]);
    setDALMP_ColumnDefs([
      {
        field: "HE",
        headerName: "HE",
        flex: 1,
        cellStyle: (params) => {
          const row = params.data;
          const hourEnding = row.HE;
          if (hourEnding === currentHE) {
            return {
              backgroundColor: "#90CAF9",
              color: "black",
              fontSize:
                DAClass === "OnPeak" || DAClass === "WePeak" ? "20px" : "16px",
            };
          } else {
            return {
              fontSize:
                DAClass === "OnPeak" || DAClass === "WePeak" ? "20px" : "16px",
            };
          }
        },
      },
      {
        field: "LMP",
        headerName: "LMP",
        flex: 1,
        cellStyle: (params) => {
          const row = params.data;
          const hourEnding = row.HE;
          if (hourEnding === currentHE) {
            return {
              backgroundColor: "#90CAF9",
              color: "black",
              fontSize:
                DAClass === "OnPeak" || DAClass === "WePeak" ? "20px" : "16px",
            };
          } else {
            return {
              fontSize:
                DAClass === "OnPeak" || DAClass === "WePeak" ? "20px" : "16px",
            };
          }
        },
      },
    ]);
  }, [currentHE, theme, DAClass]);

  useEffect(() => {
    setPJMDispatchLMP_ColumnDefs([
      {
        field: "Interval",
        headerName: "Time",
        flex: 1.2,
        cellStyle: (params) => ({
          fontSize: "16px",
        }),
      },
      {
        field: "Price",
        headerName: "Dispatch",
        flex: 1.3,
        cellStyle: (params) => {
          // Get the previous row value for the comparison
          const previousRow = params.api.getDisplayedRowAtIndex(
            params.rowIndex + 1,
          );

          const previousPrice = previousRow ? previousRow.data.Price : null;
          const currentPrice = params.value;

          // Compare current row value with previous row's value
          if (previousPrice !== null) {
            // Convert to numbers to ensure we are comparing numbers, not strings
            const currentPriceNum = Number(currentPrice);
            const previousPriceNum = Number(previousPrice);

            // Check if both values are valid numbers before proceeding
            if (!isNaN(currentPriceNum) && !isNaN(previousPriceNum)) {
              if (currentPriceNum < previousPriceNum) {
                return {
                  fontSize: "16px",
                  color: "#FF0000",
                  backgroundColor:
                    theme.palette.mode === "light" ? "#fff" : "#2a2a2a",
                }; // If current < previous, color red
              } else if (currentPriceNum > previousPriceNum) {
                return {
                  fontSize: "16px",
                  color: "#30bf35",
                  backgroundColor:
                    theme.palette.mode === "light" ? "#fff" : "#2a2a2a",
                }; // If current > previous, color green
              }
            }
          }

          return { fontSize: "16px" }; // No style if no previous value to compare
        },
      },
      {
        field: "LMP",
        headerName: "LMP",
        flex: 1,
        cellStyle: (params) => {
          // Get the previous row value for the comparison
          const previousRow = params.api.getDisplayedRowAtIndex(
            params.rowIndex + 1,
          );
          const previousLMP = previousRow ? previousRow.data.LMP : null;
          const currentLMP = params.value;

          // Compare current row value with previous row's value for LMP
          if (previousLMP !== null) {
            // Convert to numbers to ensure we are comparing numbers, not strings
            const currentLMPNum = Number(currentLMP);
            const previousLMPNum = Number(previousLMP);

            // Check if both values are valid numbers before proceeding
            if (!isNaN(currentLMPNum) && !isNaN(previousLMPNum)) {
              if (currentLMPNum < previousLMPNum) {
                return {
                  fontSize: "16px",
                  color: "#FF0000",
                  backgroundColor:
                    theme.palette.mode === "light" ? "#fff" : "#2a2a2a",
                }; // If current < previous, color red
              } else if (currentLMPNum > previousLMPNum) {
                return {
                  fontSize: "16px",
                  color: "#30bf35",
                  backgroundColor:
                    theme.palette.mode === "light" ? "#fff" : "#2a2a2a",
                }; // If current > previous, color green
              }
            }
          }

          return { fontSize: "16px" }; // No style if no previous value to compare
        },
      },
    ]);
    setPJMDispatch_ColumnDefs([
      {
        field: "StartTime",
        headerName: "Start Time",
        flex: 1,
        cellStyle: (params) => ({
          fontSize: "16px",
        }),
      },
      {
        field: "EndTime",
        headerName: "End Time",
        flex: 1,
        cellStyle: (params) => ({
          fontSize: "16px",
        }),
      },
      {
        field: "Price",
        headerName: "Price",
        flex: 1,
        cellStyle: (params) => {
          // Get the previous row value for the comparison
          const previousRow = params.api.getDisplayedRowAtIndex(
            params.rowIndex + 1,
          );

          const previousPrice = previousRow ? previousRow.data.Price : null;
          const currentPrice = params.value;

          // Compare current row value with previous row's value
          if (previousPrice !== null) {
            // Convert to numbers to ensure we are comparing numbers, not strings
            const currentPriceNum = Number(currentPrice);
            const previousPriceNum = Number(previousPrice);

            // Check if both values are valid numbers before proceeding
            if (!isNaN(currentPriceNum) && !isNaN(previousPriceNum)) {
              if (currentPriceNum < previousPriceNum) {
                return {
                  fontSize: "16px",
                  color: "#FF0000",
                  backgroundColor:
                    theme.palette.mode === "light" ? "#fff" : "#2a2a2a",
                }; // If current < previous, color red
              } else if (currentPriceNum > previousPriceNum) {
                return {
                  fontSize: "16px",
                  color: "#30bf35",
                  backgroundColor:
                    theme.palette.mode === "light" ? "#fff" : "#2a2a2a",
                }; // If current > previous, color green
              }
            }
          }

          return { fontSize: "16px" }; // No style if no previous value to compare
        },
      },
    ]);
  }, [theme]);

  const isValidNumber = (v) => !Number.isNaN(Number(v));
  const onCellKeyDown = useCallback((params) => {
    const event = params.event;
    const key = event.key;

    if (key !== "ArrowUp" && key !== "ArrowDown") return;

    // only for editable column
    if (!params.column.getColDef().editable) return;

    const api = params.api;
    const column = params.column;
    const rowIndex = params.node.rowIndex;

    event.preventDefault();
    event.stopPropagation();

    // stop editing current cell
    api.stopEditing();

    let nextRowIndex = key === "ArrowDown" ? rowIndex + 1 : rowIndex - 1;

    if (nextRowIndex < 0 || nextRowIndex >= api.getDisplayedRowCount()) {
      return;
    }

    // focus next cell
    api.setFocusedCell(nextRowIndex, column);

    // start editing after focus is applied
    setTimeout(() => {
      api.startEditingCell({
        rowIndex: nextRowIndex,
        colKey: column.getColId(),
      });
    }, 0);
  }, []);

  const onCellFocused = (params) => {
    if (!params.column) return;

    const colDef = params.column.getColDef();
    if (!colDef.editable) return;

    // delay editing until grid finishes focus cycle
    requestAnimationFrame(() => {
      params.api.startEditingCell({
        rowIndex: params.rowIndex,
        colKey: params.column.getColId(),
      });
    });
  };

  const resetCustom = () => {
    setCustomModifiedRows([]);
    funcModifyCustom(false);
  };
  return (
    <Stack>
      <BackgroundContainer>
        {isMobile ? (
          <Stack
            spacing={3}
            sx={{
              px: 3,
              py: 1.5,
              justifyContent: "space-between",
              display: "flex",
            }}
          >
            {/* First row: Class Selector and Date Picker */}
            <Stack direction="row" spacing={2}>
              <Stack sx={{ width: "50%" }}>
                <Typography sx={{ fontSize: "14px" }}>Class:</Typography>
                <Select
                  value={DAClass}
                  onChange={(e) => setDAClass(e.target.value)}
                  size="small"
                >
                  <MenuItem value="OnPeak">ONPEAK</MenuItem>
                  <MenuItem value="OffPeak">OFFPEAK</MenuItem>
                  <MenuItem value="WePeak">WEPEAK</MenuItem>
                  <MenuItem value="ATC">ATC</MenuItem>
                </Select>
              </Stack>
              <Stack sx={{ width: "50%" }}>
                <Typography sx={{ fontSize: "14px" }}>
                  Choose a date:
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <FormControl fullWidth>
                    <Stack direction="column" spacing={0.25}>
                      <DesktopDatePicker
                        value={flowDate}
                        onChange={(date) => setflowDate(dayjs(date))}
                        views={["year", "month", "day"]}
                        openTo="day"
                        slotProps={{ textField: { size: "small" } }}
                      />
                    </Stack>
                  </FormControl>
                </LocalizationProvider>
              </Stack>
            </Stack>

            {/* Second row: ACE and Dispatch */}
            <Stack direction="row" spacing={1} justifyContent={"space-between"}>
              <Stack>
                <Typography sx={{ fontSize: "14px" }}>ACE:</Typography>
                <Typography sx={{ fontSize: "14px", display: "flex" }}>
                  {PJMAce}({PJMAceDelta}) @{PJMAceTimestamp} EST
                </Typography>
              </Stack>
              <Stack>
                <Typography sx={{ fontSize: "14px" }}>Dispatch:</Typography>
                <Typography sx={{ fontSize: "14px", display: "flex" }}>
                  {PJMLatestDispatchTimestamp} EST ({countdown})
                </Typography>
              </Stack>
            </Stack>

            {/* Third row: Checkboxes and DA Hybrid */}

            <Stack spacing={2} direction={"row"} justifyContent="space-between">
              <Stack>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={useDAHybrid}
                      onChange={(e) => setuseDAHybrid(e.target.checked)}
                    />
                  }
                  label="Use DAHybrid"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={useDispatch}
                      onChange={(e) => setuseDispatch(e.target.checked)}
                    />
                  }
                  label="Use Dispatch"
                />
              </Stack>
              <Stack>
                <Typography sx={{ fontSize: "14px" }}>
                  Hour Ending: {currentHE}
                </Typography>
                <Typography sx={{ fontSize: "14px", display: "flex" }}>
                  DA Hybrid: {DAHybrid}
                </Typography>
              </Stack>
            </Stack>
            <Stack>
              <Typography sx={{ fontSize: "14px" }}>
                Current Time: &nbsp;
                {CurrentTime} CST
              </Typography>
            </Stack>
            {/* Last row: Current Time */}
          </Stack>
        ) : (
          <Box
            sx={{
              px: 30,
              py: 1.5,
              display: "grid",
              gridTemplateColumns: `
          120px
          240px
          160px
          240px
          180px
          170px
          180px
        `,
              columnGap: 3,
              alignItems: "start",
            }}
          >
            {/* CLASS */}
            <Box>
              <Typography fontSize={14}>Class:</Typography>
              <Select
                value={DAClass}
                onChange={(e) => setDAClass(e.target.value)}
                size="small"
                sx={{ width: 100 }}
              >
                <MenuItem value="OnPeak">ONPEAK</MenuItem>
                <MenuItem value="OffPeak">OFFPEAK</MenuItem>
                <MenuItem value="WePeak">WEPEAK</MenuItem>
                <MenuItem value="ATC">ATC</MenuItem>
              </Select>
            </Box>

            {/* ACE */}
            <Box>
              <Typography fontSize={14}>ACE:</Typography>
              <Typography
                fontSize={14}
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                <strong>{PJMAce}</strong> ({PJMAceDelta}) @{PJMAceTimestamp} EST
              </Typography>
            </Box>

            {/* CURRENT TIME */}
            <Box>
              <Typography fontSize={14}>Current Time:</Typography>
              <Typography fontSize={14} whiteSpace="nowrap">
                {CurrentTime} CST
              </Typography>
            </Box>

            {/* DISPATCH */}
            <Box>
              <Typography fontSize={14}>Dispatch:</Typography>
              <Typography fontSize={14} whiteSpace="nowrap">
                {PJMLatestDispatchTimestamp} EST ({countdown})
              </Typography>
            </Box>

            {/* CHECKBOXES */}
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={useDAHybrid}
                    onChange={(e) => setuseDAHybrid(e.target.checked)}
                  />
                }
                label="Use DAHybrid"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={useDispatch}
                    onChange={(e) => setuseDispatch(e.target.checked)}
                  />
                }
                label="Use Dispatch"
              />
            </Box>

            {/* HE / HYBRID */}
            <Box>
              <Typography fontSize={14} whiteSpace="nowrap">
                Hour Ending: {currentHE}
              </Typography>
              <Typography fontSize={14} whiteSpace="nowrap">
                DA Hybrid: {DAHybrid}
              </Typography>
            </Box>

            {/* DATE PICKER */}
            <Box>
              <CustomDatePicker
                mode="single"
                label="Choose a date:"
                value={flowDate}
                onChange={(date) => setflowDate(dayjs(date))}
              />
            </Box>
          </Box>
        )}
        {/* Right section */}
      </BackgroundContainer>
      <Stack sx={{ width: "100%", overflowX: "hidden" }} spacing={2}>
        <Stack
          direction={isMobile ? "column" : "row"}
          spacing={2}
          sx={{ width: "100%", overflowX: "hidden", px: 2, pt: 2 }} // Ensure the row doesn't overflow horizontally
        >
          {/* Day-Ahead Section */}
          <Stack
            sx={{
              padding: 0,
              borderRadius: 2,
              py: 2,
              display: "flex",
              flex: 1.12,
              minWidth: 0,
              overflowX: "hidden", // Prevent horizontal overflow
              px: 1,
              border:
                theme.palette.mode === "dark"
                  ? "1px solid #ffffffbd"
                  : "1px solid #222222bd",
            }}
            spacing={1}
          >
            <Typography sx={{ textAlign: "center", fontSize: "18px" }}>
              Day-Ahead
            </Typography>
            <Stack
              direction="row"
              spacing={2}
              sx={{
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {isValidNumber(DALMP_AVG) && (
                <Typography sx={{ textAlign: "center", fontSize: "30px" }}>
                  {DALMP_AVG}
                </Typography>
              )}
            </Stack>

            {/* ------- TABLE SECTION ------- */}
            <Stack
              sx={{
                overflowX: "hidden !important",
              }}
            >
              <CommonAgGrid
                rowData={DayAheadRowData}
                columnDefs={DALMP_ColumnDefs}
                className="custom-grid-theme"
                defaultColumnDefs={{
                  filter: true,
                }}
                rowHeight={
                  DAClass === "OnPeak" || DAClass === "WePeak" ? 40 : 30
                }
                headerHeight={30}
                domLayout="autoHeight"
                style={{ width: "100%" }} // Ensure grid takes full width
              />
            </Stack>
          </Stack>

          {/* Hybrid Section */}
          <Stack
            sx={{
              padding: 0,
              borderRadius: 2,
              py: 2,
              display: "flex",
              flex: 1.12,
              minWidth: 0,
              overflowX: "hidden", // Prevent horizontal overflow
              px: 1,
              border:
                theme.palette.mode === "dark"
                  ? "1px solid #ffffffbd"
                  : "1px solid #222222bd",
            }}
            spacing={1}
          >
            <Typography sx={{ textAlign: "center", fontSize: "18px" }}>
              Hybrid
            </Typography>
            <Stack
              direction="row"
              spacing={2}
              sx={{
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {isValidNumber(Hybrid_AVG) && (
                <Typography sx={{ textAlign: "center", fontSize: "30px" }}>
                  {Hybrid_AVG}
                </Typography>
              )}
            </Stack>

            {/* ------- TABLE SECTION ------- */}
            <Stack
              sx={{
                overflowX: "hidden !important",
              }}
              spacing={2}
            >
              <CommonAgGrid
                rowData={HybridRowData}
                columnDefs={Hybrid_ColumnDefs}
                className="custom-grid-theme"
                domLayout="autoHeight"
                defaultColumnDefs={{
                  filter: true,
                }}
                rowHeight={
                  DAClass === "OnPeak" || DAClass === "WePeak" ? 40 : 30
                }
                headerHeight={30}
                style={{ width: "100%" }} // Ensure grid takes full width
              />
              <Typography>✓ Indicates Integrated LMP</Typography>
            </Stack>
          </Stack>

          {/* Custom Section */}
          <Stack
            sx={{
              padding: 0,
              borderRadius: 2,
              py: 2,
              display: "flex",
              flex: 1.12,
              minWidth: 0,
              overflowX: "hidden", // Prevent horizontal overflow
              px: 1,
              border:
                theme.palette.mode === "dark"
                  ? "1px solid #ffffffbd"
                  : "1px solid #222222bd",
            }}
            spacing={0.5}
          >
            <Stack
              direction="row"
              spacing={0}
              justifyContent="center"
              alignItems="center"
            >
              <Typography sx={{ textAlign: "center", fontSize: "18px" }}>
                Custom
              </Typography>
              <IconButton onClick={resetCustom}>
                <Tooltip title="Reset Custom">
                  <RestoreIcon sx={{ fontSize: "30px" }} />
                </Tooltip>
              </IconButton>
            </Stack>
            <Stack
              direction="row"
              spacing={2}
              sx={{
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography sx={{ textAlign: "center", fontSize: "30px" }}>
                {Custom_AVG}
              </Typography>
            </Stack>

            {/* ------- TABLE SECTION ------- */}
            <Stack
              sx={{
                overflowX: "hidden !important",
              }}
              spacing={2}
            >
              <CommonAgGrid
                rowData={Custom_RowData}
                columnDefs={Custom_ColumnDefs}
                className="custom-grid-theme"
                defaultColumnDefs={{
                  filter: true,
                }}
                rowHeight={
                  DAClass === "OnPeak" || DAClass === "WePeak" ? 40 : 30
                }
                headerHeight={30}
                domLayout="autoHeight"
                style={{ width: "100%" }} // Ensure grid takes full width
                onCellValueChanged={handleCellValueChanged}
                onCellKeyDown={onCellKeyDown}
                onCellFocused={onCellFocused}
              />
              <Typography>
                Prices are calculated using RT LMP, Dispatch, and DA LMPs.
              </Typography>
            </Stack>
          </Stack>

          {/* Latest Dispatch Section */}
          <Stack
            sx={{
              padding: 0,
              borderRadius: 2,
              py: 2,
              display: "flex",
              flex: 1.5,
              minWidth: 0,
              overflowX: "hidden", // Prevent horizontal overflow
              px: 1,
              border:
                theme.palette.mode === "dark"
                  ? "1px solid #ffffffbd"
                  : "1px solid #222222bd",
            }}
            spacing={1}
          >
            <Typography sx={{ textAlign: "center", fontSize: "18px" }}>
              Latest Dispatch
            </Typography>
            <Stack
              direction="row"
              spacing={2}
              sx={{
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  textAlign: "center",
                  fontSize: "30px",
                  color: LatestDispatchDelta > 0 ? "#009211" : "#FF0000",
                }}
              >
                {LatestDispatchPrice} ({LatestDispatchDelta})
              </Typography>
            </Stack>

            {/* ------- TABLE SECTION ------- */}
            <Stack
              sx={{
                overflowX: "hidden !important",
              }}
              spacing={2}
            >
              <CommonAgGrid
                rowData={LatestDispatch}
                columnDefs={LatestDispatch_ColumnDefs}
                className="custom-grid-theme"
                defaultColumnDefs={{
                  filter: true,
                }}
                rowHeight={30}
                headerHeight={30}
                domLayout="autoHeight"
                style={{ width: "100%" }} // Ensure grid takes full width
              />
              <Typography>
                West Hub Dispatch = (PENELEC x 0.57) + (PEP x 0.43)
              </Typography>
            </Stack>
          </Stack>

          {/* Dispatch Section */}
          <Stack
            sx={{
              padding: 0,
              borderRadius: 2,
              py: 2,
              display: "flex",
              flex: 1.8,
              minWidth: 0,
              overflowX: "hidden", // Prevent horizontal overflow
              px: 1,
              border:
                theme.palette.mode === "dark"
                  ? "1px solid #ffffffbd"
                  : "1px solid #222222bd",
            }}
            spacing={2}
          >
            <Typography sx={{ textAlign: "center", fontSize: "18px" }}>
              Dispatch
            </Typography>
            <Stack
              direction="row"
              spacing={2}
              sx={{
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography sx={{ textAlign: "center", fontSize: "20px" }}>
                Latest Timestamp: {PJMLatestDispatchTimestamp}
              </Typography>
            </Stack>

            {/* ------- TABLE SECTION ------- */}
            <Stack
              sx={{
                height: "70vh",
                overflowX: "hidden !important",
              }}
              spacing={2}
            >
              <CommonAgGrid
                rowData={PJMDispatch}
                columnDefs={PJMDispatch_ColumnDefs}
                className="custom-grid-theme"
                defaultColumnDefs={{
                  filter: true,
                }}
                pagination={true}
                paginationPageSize={isMobile ? 19 : 18}
                paginationPageSizeSelector={false}
                rowHeight={30}
                headerHeight={30}
                style={{ width: "100%" }} // Ensure grid takes full width
              />
              <Typography>Times are EPT Actual timestamps</Typography>
            </Stack>
          </Stack>

          {/* Dispatch/LMP Section */}
          <Stack
            sx={{
              padding: 0,
              borderRadius: 2,
              py: 2,
              display: "flex",
              flex: 1.8,
              minWidth: 0,
              overflowX: "hidden", // Prevent horizontal overflow
              px: 1,
              border:
                theme.palette.mode === "dark"
                  ? "1px solid #ffffffbd"
                  : "1px solid #222222bd",
            }}
            spacing={6.7}
          >
            <Typography sx={{ textAlign: "center", fontSize: "18px" }}>
              Dispatch/LMP
            </Typography>

            {/* ------- TABLE SECTION ------- */}
            <Stack
              sx={{
                height: "70vh",
                overflowX: "hidden !important",
              }}
              spacing={2}
            >
              <CommonAgGrid
                rowData={PJMDispatchLMPs}
                columnDefs={PJMDispatchLMP_ColumnDefs}
                className="custom-grid-theme"
                defaultColumnDefs={{
                  filter: true,
                }}
                pagination={true}
                paginationPageSize={18}
                rowHeight={30}
                headerHeight={30}
                paginationPageSizeSelector={false}
                style={{ width: "100%" }} // Ensure grid takes full width
              />
              <Typography>Times are interval ending EPT</Typography>
            </Stack>
          </Stack>
        </Stack>

        {/* Below Section */}
        <Stack sx={{ paddingX: 2, py: 1, overflowX: "hidden" }}>
          <Stack direction={"row"} spacing={3}>
            <Typography sx={{ fontWeight: 600 }}>
              DA Load: {DAPublishedTime_CST}
            </Typography>
            <Typography sx={{ fontWeight: 600 }}>
              Latest Load Forecast: {LatestPublishedTime_CST}
            </Typography>
          </Stack>
          <Stack sx={{ height: isMobile ? "230px" : "210px" }}>
            <CommonAgGrid
              rowData={LoadForecastDataPJM}
              columnDefs={LoadForecastPJM_ColumnDefs}
              className="custom-grid-theme"
              defaultColumnDefs={{
                filter: true,
              }}
              style={{ width: "100%" }} // Ensure grid takes full width
            />
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default PJM;
